import { type FC, memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/components/podkit/lib/cn";

type AnnouncementMarkdownProps = {
    content: string;
    className?: string;
};

/**
 * A restricted Markdown renderer for announcement banners.
 * - Disables images for security (prevents tracking/phishing)
 * - Only allows basic text formatting and links
 */
const AnnouncementMarkdownBase: FC<AnnouncementMarkdownProps> = ({ content, className = "" }) => {
    if (!content) return null;

    const components: Components = {
        // Disable images entirely
        img: () => null,
        // Basic text formatting
        p: ({ children }) => <span>{children}</span>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        // Links open in new tab with security attributes
        a: ({ children, href }) => (
            <a href={href} className="underline hover:no-underline" target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        ),
        // Disable block-level elements that don't make sense in a banner
        h1: ({ children }) => <span className="font-semibold">{children}</span>,
        h2: ({ children }) => <span className="font-semibold">{children}</span>,
        h3: ({ children }) => <span className="font-semibold">{children}</span>,
        ul: ({ children }) => <span>{children}</span>,
        ol: ({ children }) => <span>{children}</span>,
        li: ({ children }) => <span>{children} </span>,
        blockquote: ({ children }) => <span>{children}</span>,
        pre: ({ children }) => <code className="rounded bg-black/10 px-1 font-mono text-sm">{children}</code>,
        code: ({ children }) => <code className="rounded bg-black/10 px-1 font-mono text-sm">{children}</code>,
    };

    return (
        <span className={cn("inline", className)}>
            <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
                {content}
            </ReactMarkdown>
        </span>
    );
};

export const AnnouncementMarkdown = memo(AnnouncementMarkdownBase, (prevProps, nextProps) => {
    return prevProps.content === nextProps.content;
});
